import React, { createContext, useState, useEffect } from 'react';
import { refreshToken as refreshTokenAPI, getUserProfile } from '../services/api';
import cacheInitializer from '../services/cacheInitializer';
import tokenManager from '../services/tokenManager';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleRoleBasedRedirect = (role, isInitialLogin = false) => {
    const currentPath = window.location.pathname;

    if (role === 'operator') {
      if (isInitialLogin || !currentPath.startsWith('/operator')) {
        window.location.href = '/operator/dashboard';
      }
    } else if (role === 'admin') {
      if (isInitialLogin || !currentPath.startsWith('/admin')) {
        window.location.href = '/admin';
      }
    }
  };

  useEffect(() => {
    const verifyToken = async () => {
      const timeout = setTimeout(() => {
        console.warn('AuthContext verification timeout, clearing loading state');
        setIsLoading(false);
      }, 10000);

      // If we have a valid in-memory token, skip refresh
      if (tokenManager.getToken() && !tokenManager.shouldRefreshToken()) {
        setIsAuthenticated(true);
        setIsLoading(false);
        clearTimeout(timeout);
        return;
      }

      // If no prior session exists, skip the refresh call entirely (avoids unnecessary 401)
      if (!tokenManager.hasSession()) {
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
        clearTimeout(timeout);
        return;
      }

      // Try to refresh using httpOnly cookie
      try {
        const response = await refreshTokenAPI();
        if (response.token && response.user) {
          tokenManager.setToken(response.token);
          setIsAuthenticated(true);
          setUser({
            ...response.user,
            subscription_status: response.user.subscription_status
          });
        } else {
          tokenManager.clearToken();
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        // No valid session - user is not authenticated
        tokenManager.clearToken();
        setIsAuthenticated(false);
        setUser(null);
      }

      setIsLoading(false);
      clearTimeout(timeout);
    };

    verifyToken();
  }, []);

  const login = async (userData, token) => {
    tokenManager.setToken(token);
    setIsAuthenticated(true);
    setUser({
      ...userData,
      subscription_status: userData.subscription_status
    });

    cacheInitializer.initializeUserData();
    handleRoleBasedRedirect(userData.role, true);
  };

  const handleLogout = async () => {
    try {
      const { logoutAPI } = await import('../services/api');
      await logoutAPI();
    } catch (error) {
      // Continue with local cleanup even if API call fails
    }

    tokenManager.clearToken();
    setIsAuthenticated(false);
    setUser(null);
    cacheInitializer.reset();
    window.location.href = '/login';
  };

  const updateUserSubscription = (status) => {
    setUser(prevUser => ({
      ...prevUser,
      subscription_status: status
    }));
  };

  const refreshUser = async () => {
    try {
      const response = await getUserProfile();
      setUser(response.user);
      return response.user;
    } catch (error) {
      console.error('Error refreshing user:', error);
      setUser(null);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      user,
      login,
      logout: handleLogout,
      updateUserSubscription,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};
